import { useEffect, useRef, useState } from "react";
import { normalizeStoreAlias, toast } from "shared-components";
import useSWR from "swr";
import { shallow } from "zustand/shallow";

import {
  loadClientShoppingCart,
  migrateCustomProductImages,
  preCheckoutCart,
  updateShoppingCartItem,
  deleteShoppingCartItem,
} from "@/api/cart";
import type { PreCheckoutResponse, ShoppingCartItem, CartItemStockInfo } from "@/api/cart/models";
import type { IInsufficientStockItem } from "shared-components/interfaces/order";
import { useUserIdentityCookie } from "@/hooks/use-user-identity-cookie";
import { useShoppingCartStore } from "@/stores/use-shopping-cart-store";
import { useUserStore } from "@/stores/use-user-store";
import { getCookie } from "@/utils/helpers";

interface UseShoppingCartOptions {
  storeAlias?: string;
  skipPreCheckout?: boolean;
}

export const useShoppingCart = ({ storeAlias, skipPreCheckout = false }: UseShoppingCartOptions = {}) => {
  const normalizedStoreAlias = normalizeStoreAlias(storeAlias);
  const { activeUser } = useUserStore();
  const migrationAttemptedForUserRef = useRef<string | null>(null);
  const { ensureUserIdentityCookie } = useUserIdentityCookie();
  const cartIdentifier = activeUser?.id || getCookie("anonymous_user_id") || null;
  const { appliedCoupon, resetForStore, setAppliedCoupon } = useShoppingCartStore(
    (state) => ({
      appliedCoupon: state.appliedCoupon,
      resetForStore: state.resetForStore,
      setAppliedCoupon: state.setAppliedCoupon,
    }),
    shallow,
  );

  useEffect(() => {
    resetForStore(normalizedStoreAlias || undefined);
  }, [normalizedStoreAlias, resetForStore]);

  const {
    data: cartData,
    isLoading: cartLoading,
    isValidating: cartValidating,
    mutate: mutateCart,
  } = useSWR(["cart/items", normalizedStoreAlias, cartIdentifier], async () => {
    const hasUserIdentityCookie = await ensureUserIdentityCookie();
    if (!hasUserIdentityCookie) {
      return { items: [] };
    }

    return loadClientShoppingCart(normalizedStoreAlias);
  });

  // PreCheckout para pegar valores de subtotal e totalDiscount
  // usa mesmo preCheckoutKey do hook useCheckout para aproveitar cache
  const storeAliasPayload = normalizedStoreAlias ? { storeAlias: normalizedStoreAlias } : {};
  const preCheckoutKey =
    skipPreCheckout || !activeUser?.id
      ? null
      : ["cart/pre-checkout", normalizedStoreAlias, cartIdentifier, appliedCoupon?.code, undefined, undefined];

  const {
    data: preCheckoutData,
    isLoading: preCheckoutLoading,
    mutate: mutatePreCheckout,
  } = useSWR<PreCheckoutResponse>(preCheckoutKey, () =>
    preCheckoutCart({
      cupon: appliedCoupon?.code,
      ...storeAliasPayload,
    }),
  );

  useEffect(() => {
    const hasPendingCustomProductImageMigration = cartData?.customProductImageMigrationInfo?.hasPending;
    if (!activeUser?.id || !hasPendingCustomProductImageMigration) {
      return;
    }
    if (migrationAttemptedForUserRef.current === activeUser.id) {
      return;
    }

    migrationAttemptedForUserRef.current = activeUser.id;
    migrateCustomProductImages().catch((error) => {
      console.error("Failed to migrate custom product images", error);
    });
  }, [activeUser?.id, cartData?.customProductImageMigrationInfo?.hasPending]);

  // Estados
  const [cartUpdateLoading, setCartUpdateLoading] = useState(false);

  // Estados derivados
  const allCartItems = cartData?.items || [];
  const insufficientStockItems = cartData?.insufficientStockItems || [];
  const adjustedItemsInfo = cartData?.adjustedItems || [];

  const itemsWithInsufficientStock: IInsufficientStockItem[] = getInsufficientStockItems(insufficientStockItems);

  const adjustedItemsList: IInsufficientStockItem[] = getInsufficientStockItems(adjustedItemsInfo);

  const selectedItems: ShoppingCartItem[] = allCartItems.filter((item) => item.isSelected);
  const isLoading = cartLoading || preCheckoutLoading || cartUpdateLoading || cartValidating;
  const hasSelectedAllItems = allCartItems.every(({ isSelected }) => isSelected) && allCartItems.length > 0;

  // Valores calculados pela API para usuário autenticado e pelo carrinho para visitante anônimo.
  const anonymousSubtotal = selectedItems.reduce((total, item) => total + item.cartProduct.price * item.amount, 0);
  const subtotal = preCheckoutData?.subtotal ?? anonymousSubtotal;
  const subtotalNoDiscount = preCheckoutData?.subtotalNoDiscount ?? anonymousSubtotal;
  const totalDiscount = preCheckoutData?.totalDiscount ?? 0;

  // Funções utilitárias
  const getAppliedCouponInItem = (itemId: string) =>
    appliedCoupon?.eligibleProductsIds.includes(itemId) ? appliedCoupon : undefined;

  /**
   * Método de uso interno do hook.
   * @description Mapeia informações de estoque insuficiente para o formato de item do carrinho
   */
  function getInsufficientStockItems(itemsInfo: CartItemStockInfo[]): IInsufficientStockItem[] {
    return itemsInfo
      .map((info: CartItemStockInfo) => {
        const cartItem = allCartItems.find((item) => item.id === info.itemId);
        if (!cartItem) {
          return null;
        }

        return {
          id: cartItem.id,
          amount: cartItem.amount,
          size: cartItem.size,
          color: cartItem.color,
          cartProduct: cartItem.cartProduct,
          isSelected: cartItem.isSelected,
        };
      })
      .filter((item): item is IInsufficientStockItem => item !== null);
  }

  // Funções de ação
  const updateCartItem = async (item: ShoppingCartItem) => {
    setCartUpdateLoading(true);

    try {
      const hasUserIdentityCookie = await ensureUserIdentityCookie();
      if (!hasUserIdentityCookie) {
        return;
      }

      const { id, amount, size, isSelected } = item;
      const res = await updateShoppingCartItem({
        id,
        amount,
        size,
        isSelected,
        ...storeAliasPayload,
      });

      if (res.error) {
        toast.error(res.error.message);
      } else {
        await mutateCart();
        await mutatePreCheckout();
      }
    } finally {
      setCartUpdateLoading(false);
    }
  };

  const toggleAllCartItems = async () => {
    if (allCartItems.length === 0) {
      return;
    }

    setCartUpdateLoading(true);

    try {
      const hasUserIdentityCookie = await ensureUserIdentityCookie();
      if (!hasUserIdentityCookie) {
        return;
      }

      const res = await Promise.all(
        allCartItems.map((item) =>
          updateShoppingCartItem({
            id: item.id,
            amount: item.amount,
            size: item.size,
            isSelected: !hasSelectedAllItems,
            ...storeAliasPayload,
          }),
        ),
      );

      const errors = res.filter(({ error }) => error?.message);

      if (errors.length) {
        errors.forEach(({ error }) => toast.error(error!.message));
      }

      await mutateCart();
      await mutatePreCheckout();
    } finally {
      setCartUpdateLoading(false);
    }
  };

  const removeCartItem = async (itemId: string) => {
    setCartUpdateLoading(true);

    try {
      const hasUserIdentityCookie = await ensureUserIdentityCookie();
      if (!hasUserIdentityCookie) {
        return;
      }

      const res = await deleteShoppingCartItem({ itemId, ...storeAliasPayload });

      if (res.error) {
        toast.error(res.error.message);
      } else {
        const remainingCartItems = allCartItems.filter((item) => item.id !== itemId);
        const hasEligibleRemainingItem = remainingCartItems.some((item) =>
          appliedCoupon?.eligibleProductsIds.includes(item.id),
        );

        if (appliedCoupon && !hasEligibleRemainingItem) {
          setAppliedCoupon(undefined);
        }

        await mutateCart();
        await mutatePreCheckout();
        toast.success("Produto excluído do carrinho com sucesso.");
      }
    } catch (error) {
      console.error(error);
    } finally {
      setCartUpdateLoading(false);
    }
  };

  return {
    // Carrinho
    allCartItems,
    insufficientStockItems,
    itemsWithInsufficientStock,
    /** Informações de estoque para itens com quantidades ajustadas por falta de estoque ou após merge de carrinho anônimo*/
    adjustedItemsInfo,
    /** Items com quantidades ajustadas por falta de estoque ou após merge de carrinho anônimo*/
    adjustedItemsList,
    selectedItems,
    hasSelectedAllItems,
    isLoading,

    // Cupom
    appliedCoupon,
    subtotal,
    subtotalNoDiscount,
    totalDiscount,

    // Ações
    applyCoupon: setAppliedCoupon,
    removeCartItem,
    toggleAllCartItems,
    updateCartItem,

    // Utilitários
    getAppliedCouponInItem,
  };
};
